import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  getMe,
  getStoredUser,
  getStoredToken,
  logout as apiLogout,
  setDefaultSite as apiSetDefaultSite,
} from '../services/api.service';
import { clearStoredScope } from '../constants/scope';
import { hasPermission as checkPermission } from '../utils/permissions';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  role_id?: string;
  permissions?: string[];
  /** Favourite site: the scope a fresh session opens on. null = All Sites. */
  default_node_id?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  logout: () => void;
  setUser: (user: User, token: string) => void;
  /** Persist my favourite site (null clears it). Resolves once the server agrees. */
  setDefaultSite: (nodeId: string | null) => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  logout: () => { },
  setUser: () => { },
  setDefaultSite: async () => { },
  hasPermission: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(getStoredUser());
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      getMe()
        .then((data) => {
          setUserState(data.user);
          localStorage.setItem('cloud_user', JSON.stringify(data.user));
        })
        .catch(() => {
          apiLogout();
          setUserState(null);
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const setUser = (user: User, token: string) => {
    setUserState(user);
    setToken(token);
  };

  /**
   * Server first, then local state — so a star that lights up is one that will
   * still be lit after the next login. Updating optimistically would show a
   * favourite that a failed request never actually saved.
   *
   * The cached copy in localStorage is updated too, because getStoredUser()
   * seeds `user` on the next mount before /auth/me has answered; leaving it
   * stale would make the star flicker off and back on at every page load.
   */
  const setDefaultSite = async (nodeId: string | null) => {
    await apiSetDefaultSite(nodeId);
    setUserState((prev) => {
      if (!prev) return prev;
      const next = { ...prev, default_node_id: nodeId };
      localStorage.setItem('cloud_user', JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    setUserState(null);
    setToken(null);
    apiLogout();
    // The selected site is part of the session, not of the browser: leaving it behind would put
    // the next person to log in here inside someone else's site, with that site's menu.
    clearStoredScope();
  };

  // return (
  //   <AuthContext.Provider value={{ user, token, loading, logout, setUser }}>
  //     {children}
  //   </AuthContext.Provider>
  // );

  const hasPermission = (permission: string): boolean => {
    return checkPermission(user, permission);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, logout, setUser, setDefaultSite, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
